import React, { useState, useEffect, useMemo, createContext } from 'react';
import PropTypes from 'prop-types';
import BZAPIService from '../services/BZAPIService';
import { initBsaProdSetting } from '../helpers/bsaConfig';

const TenantSettingsContext = createContext({
  bsaEnabled: true,
  bsaProd: true,
  planEnabled: true,
  preventEnabled: true,
  predictEnabled: true,
  bugCoverageEnabled: true,
  setBugCoverageEnabled: () => {},
  isLoaded: false
});

const TenantSettingsProvider = ({ children }) => {
  const [bsaEnabled, setBsaEnabled] = useState(true);
  const [bsaProd, setBsaProd] = useState(true);
  const [planEnabled, setPlanEnabled] = useState(true);
  const [preventEnabled, setPreventEnabled] = useState(true);
  const [predictEnabled, setPredictEnabled] = useState(true);
  const [bugCoverageEnabled, setBugCoverageEnabled] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const [bsaEnabledRes, bsaProdRes, planRes, preventRes, predictRes, bugCoverageRes] = await Promise.allSettled([
        BZAPIService.getTenantSetting('bsaEnabled'),
        BZAPIService.getTenantSetting('bsaProd'),
        BZAPIService.getTenantSetting('planEnabled'),
        BZAPIService.getTenantSetting('preventEnabled'),
        BZAPIService.getTenantSetting('predictEnabled'),
        BZAPIService.getTenantSetting('bugCoverageEnabled')
      ]);

      if (cancelled) return;

      if (bsaEnabledRes.status === 'fulfilled') {
        setBsaEnabled(bsaEnabledRes.value?.value?.value !== false);
      } else {
        console.warn('Failed to fetch bsaEnabled setting, defaulting to enabled:', bsaEnabledRes.reason);
      }

      const bsaProdValue = bsaProdRes.status === 'fulfilled' ? bsaProdRes.value?.value?.value !== false : true;
      if (bsaProdRes.status !== 'fulfilled') {
        console.warn('Failed to fetch bsaProd setting, defaulting to prod:', bsaProdRes.reason);
      }
      setBsaProd(bsaProdValue);
      initBsaProdSetting(bsaProdValue);

      if (planRes.status === 'fulfilled') {
        setPlanEnabled(planRes.value?.value?.value !== false);
      }
      if (preventRes.status === 'fulfilled') {
        setPreventEnabled(preventRes.value?.value?.value !== false);
      }
      if (predictRes.status === 'fulfilled') {
        setPredictEnabled(predictRes.value?.value?.value !== false);
      }
      // Super Admin "Bug coverage" switch (STRY0038808). A missing row 404s
      // and leaves the default: on.
      if (bugCoverageRes.status === 'fulfilled') {
        setBugCoverageEnabled(bugCoverageRes.value?.value?.value !== false);
      }

      setIsLoaded(true);
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(
    () => ({
      bsaEnabled,
      bsaProd,
      planEnabled,
      preventEnabled,
      predictEnabled,
      bugCoverageEnabled,
      setBugCoverageEnabled,
      isLoaded
    }),
    [
      bsaEnabled,
      bsaProd,
      planEnabled,
      preventEnabled,
      predictEnabled,
      bugCoverageEnabled,
      setBugCoverageEnabled,
      isLoaded
    ]
  );

  return <TenantSettingsContext.Provider value={value}>{children}</TenantSettingsContext.Provider>;
};

TenantSettingsProvider.propTypes = { children: PropTypes.node };

export { TenantSettingsContext, TenantSettingsProvider };
